import React, { useState } from 'react';
import { TripMatch, TripMatchStatus } from '@marageti/z4-sdk/lib/travel';
import {
  ContextMenuComplete,
  useApiClient,
  useFetch,
  TripMatchChips,
  OtherChips,
  BackButton,
} from '@marageti/z4-lib';
import { useParams, Navigate } from 'react-router-dom';
import {
  Skeleton, Stack, Typography, useMediaQuery,
} from '@mui/material';
import { useConfirm } from 'material-ui-confirm';
import { useSnackbar } from 'notistack';
import { ReferralType } from '@marageti/z4-sdk/lib/travel/TripRequest';
import SubNavigation from '../../../components/sub-navigation';
import { useStandardsStore, useAuthStore } from '../../../store';
import { formatCountryCodes } from '../../../utils/helpers';
import { TabItem } from '../../../components/sub-navigation/types';
import { MinimalChatIndicator } from '../../../components/trips-card/trip-indicators';
import CancelSoldTripFormDialog from '../../../components/zicasso-dialogs/cancel-sold-trip/cancel-sold-trip-form-dialog';

const { ChipRepeat, ChipPRL } = OtherChips;
const { getTripMatchStatusChip } = TripMatchChips;

type ViewProps = {
  tabs: TabItem[];
  onClickPostpone: () => void;
  onClickInactive: () => void;
  onClickCancelSold: () => void;
};

const tagIcons = {
  RPT: <ChipRepeat />,
  PRL: <ChipPRL />,
};

const getLinks = (tripMatch: TripMatch | undefined, loggedInAgentId: string | undefined): TabItem[] => {
  if (!tripMatch || !loggedInAgentId) {
    return [];
  }

  return [
    {
      e2e: 'ztest-settings-details',
      to: 'details',
      label: 'details',
    },
    {
      e2e: 'ztest-settings-tab-messages',
      to: 'messages',
      label: 'messages',
      endAdornment: (
        <MinimalChatIndicator
          loggedInAgentId={loggedInAgentId}
          tripMessageState={tripMatch.tripMessageState}
        />
      ),
    },
    {
      e2e: 'ztest-settings-tab-documents',
      to: 'documents',
      label: 'documents',
    },
    {
      e2e: 'ztest-settings-tab-reviews',
      to: 'review',
      label: 'review',
    },
    {
      e2e: 'ztest-settings-tab-invoice',
      to: 'sales',
      label: 'sales',
    },
  ];
};

const TripSecondaryAppbarView = ({
  tabs,
  onClickPostpone,
  onClickInactive,
  onClickCancelSold,
}: ViewProps) => {
  const apiClient = useApiClient();
  const { tripMatchId } = useParams<{ tripMatchId: string }>();
  const { countryCodes } = useStandardsStore();

  const {
    data: tripMatch,
  } = useFetch<TripMatch>(apiClient.tripMatchesClient.getTripMatch, [tripMatchId], true);

  const traveler = tripMatch?.travelers[0];
  const travelerName = traveler && traveler.fullName;
  const countries = tripMatch && countryCodes ? formatCountryCodes(tripMatch.countries, countryCodes) : <Skeleton width={100} />;
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('desktop'));

  const isPrlReferral = tripMatch && (
    tripMatch.referralType === ReferralType.OneTimePartnerReferral
    || tripMatch.referralType === ReferralType.LifetimePartnerReferral
  );

  const tripTitleElement = travelerName ? (
    <Typography
      component="span"
      variant="h3"
      fontWeight="bold"
      color="primary.main"
    >
      {`${travelerName}'s Trip`}
    </Typography>
  ) : <Skeleton width={250} />;

  const tripTagsRow = tripMatch ? (
    <Stack direction="row" spacing={0.75} alignItems="center">
      {tripMatch.status ? getTripMatchStatusChip(tripMatch.status, 'small') : null}
      {tripMatch.repeatTraveler ? tagIcons.RPT : null}
      {isPrlReferral ? tagIcons.PRL : null}
    </Stack>
  ) : null;

  const hasTripTags = !!tripMatch?.status || !!tripMatch?.repeatTraveler || !!isPrlReferral;
  const getContextMenuItems = () => {
    const items = [];

    if (tripMatch?.status && [TripMatchStatus.MatchActive].includes(tripMatch.status)) {
      items.push({
        action: onClickPostpone,
        label: 'Postpone',
      });
    }

    if (tripMatch?.status && [TripMatchStatus.MatchActive].includes(tripMatch.status)) {
      items.push({
        action: onClickInactive,
        label: 'Mark as Inactive',
      });
    }

    if (tripMatch?.status === TripMatchStatus.MatchSold) {
      items.push({
        action: onClickCancelSold,
        label: 'Cancel Trip',
      });
    }

    return items;
  };

  const contextMenuItems = getContextMenuItems();

  return (
    <SubNavigation
      tabs={tabs}
      profile={{
        primary: isDesktop ? (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {tripTitleElement}
            {tripTagsRow}
          </Stack>
        ) : tripTitleElement,
        secondary: countries,
        ...(!isDesktop && hasTripTags && tripTagsRow ? { decoration: tripTagsRow } : {}),
      }}
      startAdornment={(
        <BackButton
          aria-label="back to trip matches"
          defaultPath="/trip-matches"
          edge="start"
        />
      )}
      endAdornment={contextMenuItems.length > 0 && (
        <ContextMenuComplete
          id="trip-match-context-menu"
          items={getContextMenuItems()}
        />
      )}
    />
  );
};

const SubNavigationTripContainer = () => {
  const confirm = useConfirm();
  const apiClient = useApiClient();
  const { enqueueSnackbar } = useSnackbar();
  const { tripMatchId } = useParams();
  const { loggedInAgent } = useAuthStore();
  const tripMatch = useFetch<TripMatch>(apiClient.tripMatchesClient.getTripMatch, [tripMatchId], !!tripMatchId);
  const [cancelSoldOpen, setCancelSoldOpen] = useState(false);

  const onClickCancelSold = () => setCancelSoldOpen(true);

  const onClickPostpone = () => {
    confirm({
      title: 'Postpone Trip?',
      description: (
        <span>
          Postponing this trip will move it to the
          {' '}
          <em>Postpone</em>
          {' '}
          section of your queue. It can be reactivated at a later date.
        </span>
      ),
      confirmationText: 'Postpone',
    })
      .then(async () => {
        if (tripMatchId && loggedInAgent?.id) {
          try {
            await apiClient.tripMatchesClient.putTripMatchStatus(tripMatchId, {
              id: tripMatchId,
              status: TripMatchStatus.MatchPostponed,
              statusInfo: {
                reason: 'Postponed by Agent',
              },
            });
            tripMatch.mutate();
            enqueueSnackbar('Trip postponed.', { variant: 'success' });
          } catch (error) {
            console.error(error);
          }
        }
      })
      .catch(() => {});
  };

  const onClickInactive = () => {
    confirm({
      title: 'Mark Trip as Inactive?',
      description: (
        <>
          Inactive trips can be reactivated in the future and remain visible to travelers, who can still send messages.
          Trips should only be marked as inactive after you have completed the follow-up timeline or the travel dates have passed.
        </>
      ),
      confirmationText: 'Confirm',
    })
      .then(async () => {
        if (tripMatchId) {
          try {
            await apiClient.tripMatchesClient.putTripMatchStatus(tripMatchId, {
              id: tripMatchId,
              status: TripMatchStatus.MatchInactive,
              statusInfo: {
                reason: 'Trip marked as inactive by Agent',
              },
            });
            tripMatch.mutate();
            enqueueSnackbar('Trip marked as inactive.', { variant: 'success' });
          } catch (error) {
            console.error(error);
          }
        }
      })
      .catch(() => {});
  };

  // New Trip Matches should be accepted before being able to navigate to trip match details
  if (tripMatch.data?.status === TripMatchStatus.MatchPending) {
    return <Navigate replace to={`/trip-matches/${tripMatchId}/accept`} />;
  }

  return (
    <>
      <TripSecondaryAppbarView
        tabs={getLinks(tripMatch.data, loggedInAgent?.id)}
        onClickPostpone={onClickPostpone}
        onClickInactive={onClickInactive}
        onClickCancelSold={onClickCancelSold}
      />
      <CancelSoldTripFormDialog
        open={cancelSoldOpen}
        closeDialog={() => setCancelSoldOpen(false)}
        tripMatchId={tripMatchId}
        onSuccess={() => tripMatch.mutate()}
      />
    </>
  );
};

export default SubNavigationTripContainer;
